/** Texts of the Illusnacci model (break on the higher timeframe, 38.2 % retest and rejection on 5M): its tuning, its summary, its chart, its checks. Each key in both languages. */
export const illusnacci = {
  en: {
    "il.name": "Illusnacci",
    "il.tune": "Tuning",
    "il.title": "Illusnacci tuning",
    "il.about.title": "Illusnacci – the strategy",
    "il.about.intro": "A mechanical two-timeframe system after your template: the higher timeframe (1H, or 15M for faster scalping) decides the structure and the break, the 5-minute chart only the confirmation and the entry. Described for short; long is mirrored.",
    "il.about.steps": "Swings: the last swing high and low on the higher timeframe (a fractal of 5 bars either side, tunable).\nBreak: a higher bar closes below the last swing low — a wick through it alone does not count. The setup is armed.\nFibonacci: from the swing high (100 %) to the broken low (0 %), as TradingView draws it — the entry line at 38.2 %, invalid beyond 50 %, the second target at −27 %.\nRetest on 5M: a bar whose high lies in the zone around the 38.2 % line (± 0.02 % of price).\nTrigger at that bar’s close: its upper wick at least half of its range, or a red bar engulfing the body of the green one before. Entry at the close.\nStop: the buffer above the retest’s highest point, never above 50 %. Target 1: the broken low — half is closed, the stop moves to the entry. Target 2: the −27 % extension.\nThe setup ends before the entry when a 5M or higher bar closes above 50 %, or when a new swing low after the break bar forms before the retest touched the zone. A newer break replaces it; one trade at a time.",
    "il.about.evidence": "Measured on gold 5M 2021–2026 with the rules fixed beforehand and real bid/ask prices: template 1H 288 trades (about one a week), 30 % winners, before costs −0.05 R per trade (no edge), after costs −0.20 R per trade; 15M scalping 1,232 trades, after costs −0.20 R, negative in every year; wick only −0.18 R. The spread costs about 0.15–0.25 R with stops near $2. Note: your template’s formulas measured 38.2 % and 50 % from the high, which would put the 50 % line below the entry; here both are measured from the broken low, as TradingView’s retracement from high to low shows them.",

    "il.htf": "Structure timeframe",
    "il.swing": "Swing (fractal)",
    "il.bars": "{n} bars either side",
    "il.buffer": "Zone and stop buffer",
    "il.pct": "± {n} %",
    "il.wickShare": "Rejection wick at least",
    "il.share": "{n} % of the range",
    "il.wick": "Trigger: wick rejection",
    "il.engulfing": "Trigger: engulfing",
    "il.preset.1": "Template (1H)",
    "il.preset.2": "Scalping (15M)",
    "il.preset.3": "Wick only",
    "il.frames": "5M · structure {htf}",
    "il.long": "Long",
    "il.short": "Short",
    "il.status": "Now",
    "il.status.idle": "waiting for a close beyond the last swing on {htf}",
    "il.status.armed": "{side} armed: waiting for the retest at 38.2 %",
    "il.status.retest": "{side} in the retest: waiting for the rejection",
    "il.status.trade": "{side} since {time}",
    "il.status.half": "{side} since {time} · half closed",
    "il.row.level": "Broken swing",
    "il.row.zone": "38.2 % zone",
    "il.row.invalid": "Invalid above / below",
    "il.row.entry": "Entry",
    "il.row.stop": "Stop",
    "il.row.stopEven": "Stop (at entry)",
    "il.row.target1": "Target 1 (half)",
    "il.row.target2": "Target 2 (−27 %)",
    "il.chart.zone": "38.2 % retest {arrow}",
    "il.chart.invalid": "50 % invalid",
    "il.chart.target1": "Target 1",
    "il.chart.target2": "Target 2 −27 %",
    "il.chart.far": "100 %",
    "il.chart.entry": "Entry {arrow}",
    "il.chart.stop": "Stop",
    "il.chart.stopEven": "Stop (entry)",
    "il.chart.setup": "Entry {arrow}",
    "il.chart.setupHint": "Illusnacci: rejection at the 38.2 % line after a close break on the higher timeframe.",
    "chk.ilBreak": "Close break",
    "chk.ilBreak.hint": "Illusnacci. ✓ sell while a setup is armed after a higher bar (1H or 15M, tuning) closed below the last swing low, or its trade runs; ✓ buy mirrored. A wick alone does not count.",
    "chk.ilRetest": "Retest at 38.2 %",
    "chk.ilRetest.hint": "Illusnacci. ✓ on its side once a 5M bar reached the zone around the 38.2 % line of the armed setup (or its trade runs).",
    "chk.ilTrigger": "Rejection, entry",
    "chk.ilTrigger.hint": "Illusnacci. ✓ on its side while the trade runs that a rejection wick or an engulfing in the zone opened.",
  },
  de: {
    "il.name": "Illusnacci",
    "il.tune": "Feintuning",
    "il.title": "Illusnacci-Feintuning",
    "il.about.title": "Illusnacci – die Strategie",
    "il.about.intro": "Ein mechanisches Zwei-Zeitebenen-System nach deiner Vorlage: Die höhere Zeitebene (1H, oder 15M für schnelleres Scalping) bestimmt Struktur und Ausbruch, der 5-Minuten-Chart nur Bestätigung und Einstieg. Beschrieben für Short; Long gespiegelt.",
    "il.about.steps": "Swings: das letzte Swing-Hoch und -Tief auf der höheren Zeitebene (Fraktal mit 5 Kerzen je Seite, einstellbar).\nAusbruch: Eine höhere Kerze schließt unter dem letzten Swing-Tief — ein Docht hindurch allein zählt nicht. Das Setup ist scharf.\nFibonacci: vom Swing-Hoch (100 %) zum gebrochenen Tief (0 %), wie TradingView es zeichnet — Einstiegslinie bei 38,2 %, ungültig über 50 %, zweites Ziel bei −27 %.\nRetest auf 5M: eine Kerze, deren Hoch in der Zone um die 38,2-%-Linie liegt (± 0,02 % des Kurses).\nAuslöser zum Schluss dieser Kerze: ihr oberer Docht mindestens die Hälfte ihrer Spanne, oder eine rote Kerze, die den Körper der grünen davor umschließt. Einstieg zum Schlusskurs.\nStop: der Puffer über dem höchsten Punkt des Retests, nie über 50 %. Ziel 1: das gebrochene Tief — die Hälfte wird geschlossen, der Stop geht auf den Einstieg. Ziel 2: die −27-%-Extension.\nDas Setup endet vor dem Einstieg, wenn eine 5M- oder höhere Kerze über 50 % schließt oder nach der Ausbruchskerze ein neues Swing-Tief entsteht, bevor der Retest die Zone berührt hat. Ein neuerer Ausbruch ersetzt es; immer nur ein Trade.",
    "il.about.evidence": "Gemessen auf Gold 5M 2021–2026 mit vorab festgelegten Regeln und echtem Geld-/Briefkurs: Vorlage 1H 288 Trades (etwa einer je Woche), 30 % Gewinner, vor Kosten −0,05 R je Trade (kein Vorteil), nach Kosten −0,20 R je Trade; Scalping 15M 1.232 Trades, nach Kosten −0,20 R, in jedem Jahr negativ; nur Docht −0,18 R. Der Spread kostet bei Stops um 2 $ etwa 0,15–0,25 R. Hinweis: Die Formeln deiner Vorlage rechneten 38,2 % und 50 % vom Hoch aus, damit läge die 50-%-Linie unter dem Einstieg; hier sind beide vom gebrochenen Tief aus gerechnet, wie TradingViews Retracement vom Hoch zum Tief sie zeigt.",

    "il.htf": "Zeitebene der Struktur",
    "il.swing": "Swing (Fraktal)",
    "il.bars": "{n} Kerzen je Seite",
    "il.buffer": "Zone und Stop-Puffer",
    "il.pct": "± {n} %",
    "il.wickShare": "Ablehnungsdocht mindestens",
    "il.share": "{n} % der Spanne",
    "il.wick": "Auslöser: Docht-Ablehnung",
    "il.engulfing": "Auslöser: Engulfing",
    "il.preset.1": "Vorlage (1H)",
    "il.preset.2": "Scalping (15M)",
    "il.preset.3": "Nur Docht",
    "il.frames": "5M · Struktur {htf}",
    "il.long": "Long",
    "il.short": "Short",
    "il.status": "Jetzt",
    "il.status.idle": "wartet auf einen Schluss jenseits des letzten Swings auf {htf}",
    "il.status.armed": "{side} scharf: wartet auf den Retest an 38,2 %",
    "il.status.retest": "{side} im Retest: wartet auf die Ablehnung",
    "il.status.trade": "{side} seit {time}",
    "il.status.half": "{side} seit {time} · Hälfte geschlossen",
    "il.row.level": "Gebrochener Swing",
    "il.row.zone": "38,2-%-Zone",
    "il.row.invalid": "Ungültig jenseits",
    "il.row.entry": "Einstieg",
    "il.row.stop": "Stop",
    "il.row.stopEven": "Stop (auf Einstand)",
    "il.row.target1": "Ziel 1 (Hälfte)",
    "il.row.target2": "Ziel 2 (−27 %)",
    "il.chart.zone": "38,2-%-Retest {arrow}",
    "il.chart.invalid": "50 % ungültig",
    "il.chart.target1": "Ziel 1",
    "il.chart.target2": "Ziel 2 −27 %",
    "il.chart.far": "100 %",
    "il.chart.entry": "Einstieg {arrow}",
    "il.chart.stop": "Stop",
    "il.chart.stopEven": "Stop (Einstand)",
    "il.chart.setup": "Einstieg {arrow}",
    "il.chart.setupHint": "Illusnacci: Ablehnung an der 38,2-%-Linie nach einem Schlussbruch auf der höheren Zeitebene.",
    "chk.ilBreak": "Schlussbruch",
    "chk.ilBreak.hint": "Illusnacci. ✓ Verkauf, solange ein Setup scharf ist, nachdem eine höhere Kerze (1H oder 15M, Feintuning) unter dem letzten Swing-Tief geschlossen hat, oder sein Trade läuft; ✓ Kauf gespiegelt. Ein Docht allein zählt nicht.",
    "chk.ilRetest": "Retest an 38,2 %",
    "chk.ilRetest.hint": "Illusnacci. ✓ auf seiner Seite, sobald eine 5M-Kerze die Zone um die 38,2-%-Linie des scharfen Setups erreicht hat (oder sein Trade läuft).",
    "chk.ilTrigger": "Ablehnung, Einstieg",
    "chk.ilTrigger.hint": "Illusnacci. ✓ auf seiner Seite, solange der Trade läuft, den ein Ablehnungsdocht oder ein Engulfing in der Zone eröffnet hat.",
  },
};
